import { useLongPress } from "use-long-press";
import { useDoubleTap } from "use-double-tap";
import { useAtom } from "jotai";
import { navigateToProjectAtom } from "../store/actions.js";
import { useTaskActions, useTasks, useMenu, useUI } from "../hooks/index.js";
import { useCallback } from "react";

const Bubble = ({ task, categoryImages, hoveredTask, setHoveredTask }) => {
  const { completeTask } = useTaskActions();
  const { setSelectedTask, setReturnedBubbles, returnedBubbles, setTasks } =
    useTasks();
  const { showContextMenu, setShowContextMenu, setContextMenuPosition } =
    useMenu();
  const { isMobile } = useUI();
  const [, navigateToProject] = useAtom(navigateToProjectAtom);

  const bubbleRightClick = useCallback(
    async (e, task) => {
      e.preventDefault();

      if (!showContextMenu) {
        setSelectedTask(task);

        if (isMobile) {
          const menuWidth = 250;
          setContextMenuPosition({
            x: window.innerWidth / 2 - menuWidth / 2,
            y: 75,
          });
        } else {
          setContextMenuPosition({
            x: Math.min(task.x, window.innerWidth - 250),
            y: Math.max(task.y + 150, 0),
          });
        }
        await setShowContextMenu(true);
      }
    },
    [
      showContextMenu,
      setSelectedTask,
      setContextMenuPosition,
      setShowContextMenu,
      isMobile,
    ]
  );

  const handleLongPress = useLongPress((e, { context }) => {
    if (isMobile) {
      bubbleRightClick(e, context);
    }
  });

  const handleDoubleTap = useDoubleTap(
    isMobile
      ? () => {
          completeTask(task);
        }
      : null
  );

  const handleSingleClick = () => {
    if (task.hasChildren) {
      navigateToProject(task.task_id);
    }
  };

  const handleDoubleClick = () => {
    if (!task.hasChildren) {
      completeTask(task);
    }

    // TODO: what if task has children?
  };

  const handleHover = (task) => setHoveredTask(task.task_id);
  const handleHoverEnd = () => setHoveredTask(null);

  const isHovering = hoveredTask === task.task_id;
  const anyHover = hoveredTask !== null;
  const backgroundImageUrl =
    categoryImages[task.category] || categoryImages.default;

  return (
    <div
      key={task.task_id}
      className={`${task.urgent ? "shaking-bubble" : ""}`}
      {...handleLongPress(task)}
      {...handleDoubleTap}
      onMouseEnter={() => handleHover(task)}
      onMouseLeave={handleHoverEnd}
      onClick={handleSingleClick}
      onDoubleClick={handleDoubleClick}
      onContextMenu={(e) => bubbleRightClick(e, task)}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("bubble", JSON.stringify(task));

        const dragImage = document.createElement("img");
        dragImage.src = categoryImages[task.category] || categoryImages.default;
        dragImage.style.width = `${task.radius * 2.5}px`;
        dragImage.style.height = `${task.radius * 2.5}px`;
        dragImage.style.borderRadius = "50%";
        dragImage.style.opacity = "0.4";

        document.body.appendChild(dragImage);
        e.dataTransfer.setDragImage(dragImage, task.radius, task.radius);
        setTimeout(() => document.body.removeChild(dragImage), 0);
      }}
      style={{
        userSelect: "none",
        position: "absolute",
        borderRadius: "50%",
        width: `${task.radius * 2}px`,
        height: `${task.radius * 2}px`,
        left: `${task.x - task.radius}px`,
        top: `${task.y - task.radius + 200}px`,
        backgroundImage: `url(${backgroundImageUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        opacity: isHovering
          ? 0.9
          : anyHover
          ? 1 - 20 / task.impact + 0.15
          : 1 - 20 / task.impact + 0.275,
        zIndex: isHovering ? 900 : anyHover ? 1 : 500,
        transform: isHovering ? "scale(1.075)" : "scale(1)",
        transition: "zIndex 0s, opacity 0.2s ease, transform 0.2s ease",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "visible",
        cursor: task.hasChildren ? "pointer" : "grab",
      }}
      title={
        task.hasChildren
          ? `Project: ${task.task_name} - Click to dive in`
          : task.due_date
          ? `Due: ${new Date(task.due_date).toLocaleDateString()}`
          : `Due Date Not Set`
      }
    >
      <span
        style={{
          position: "absolute",
          display: "-webkit-box",
          width: isHovering
            ? `${Math.min(task.radius * 6, window.innerWidth / 4)}px`
            : `${task.radius * 1.5}px`,
          lineHeight: isHovering ? "1.4rem" : `${task.radius / 3}px`,
          maxHeight: isHovering ? "4.2rem" : `${task.radius}px`,
          fontSize: isHovering
            ? "1.2rem"
            : `${Math.max(0.7, task.impact * 0.015)}rem`,
          opacity: isHovering ? 1 : anyHover ? 0.3 : 0.9,
          whiteSpace: "normal",
          overflow: "hidden",
          textAlign: "center",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          textOverflow: "ellipsis",
          transition: "opacity 0.3s ease",
        }}
        className={`bubble-text ${isHovering ? "marquee-hover" : ""}`}
      >
        {task.task_name}
      </span>
      {task.status === "stuck" ? (
        <img
          src={`${process.env.PUBLIC_URL}/media/stuck.png`}
          alt="Stuck"
          style={{
            position: "relative",
            bottom: `calc(-${task.radius}px + 16px)`,
            width: "28px",
            height: "28px",
          }}
        />
      ) : null}
      {task.isActive > 0 ? (
        <img
          src={`${process.env.PUBLIC_URL}/media/timer.png`}
          alt="Task currently being timed"
          style={{
            position: "relative",
            bottom: `calc(-${task.radius}px + 16px)`,
            width: "28px",
            height: "28px",
          }}
        />
      ) : null}
      {task.hasChildren ? (
        <img
          src={`${process.env.PUBLIC_URL}/media/project_bubbles.svg`}
          alt="Project with subtasks"
          style={{
            position: "relative",
            bottom: `calc(-${task.radius}px + 16px)`,
            left: `calc(${task.radius}px - 16px)`,
            width: "28px",
            height: "28px",
          }}
        />
      ) : null}
    </div>
  );
};

export default Bubble;
